import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

const ACTIVITY_TYPE_OPTIONS = [
  { value: 'arrival', label: 'Arrival' },
  { value: 'departure', label: 'Departure' },
  { value: 'break', label: 'Break' },
  { value: 'incident', label: 'Incident' },
  { value: 'note', label: 'Note' },
];

const ROLE_LABELS: Record<string, string> = {
  cashier: 'Cashier',
  shift_lead: 'Shift Lead',
  stock_clerk: 'Stock Clerk',
  manager: 'Manager',
};

interface EmployeeDetail {
  id: number;
  full_name: string;
  role: string;
  phone?: string;
  photo_url?: string;
  store_id: number;
  status?: string;
  recent_activity?: Activity[];
}

interface Activity {
  id: number;
  activity_type: string;
  notes?: string;
  created_at: string;
  recorded_by?: string;
}

function getInitials(name: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatRole(role: string): string {
  return ROLE_LABELS[role] || role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function activityIcon(type: string): string {
  switch (type) {
    case 'arrival': return '→';
    case 'departure': return '←';
    case 'break': return '◦';
    case 'incident': return '!';
    case 'note': return '✎';
    default: return '•';
  }
}

function activityColor(type: string): string {
  switch (type) {
    case 'arrival': return 'var(--status-success)';
    case 'departure': return 'var(--text-secondary)';
    case 'break': return 'var(--status-warning)';
    case 'incident': return 'var(--status-critical)';
    case 'note': return 'var(--accent)';
    default: return 'var(--text-secondary)';
  }
}

export default function EmployeeDetailPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();

  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Log activity form state
  const [activityType, setActivityType] = useState('arrival');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Deactivate confirm
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const fetchEmployee = useCallback(async () => {
    try {
      const res = await api.get(`/employees/${employeeId}`);
      setEmployee(res.data);
      setActivities(res.data.recent_activity || []);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load employee.'));
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  async function handleLogActivity(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.post(`/employees/${employeeId}/activity`, {
        activity_type: activityType,
        notes: notes.trim() || undefined,
      });
      setNotes('');
      // Refresh activities
      const res = await api.get(`/employees/${employeeId}`);
      setEmployee(res.data);
      setActivities(res.data.recent_activity || []);
    } catch (err: any) {
      setSubmitError(extractErrorMessage(err, 'Failed to log activity.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeactivate() {
    setDeactivating(true);
    try {
      await api.delete(`/employees/${employeeId}`);
      navigate(`/stores/${employee!.store_id}`);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to deactivate employee.'));
      setShowDeactivateConfirm(false);
      setDeactivating(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <main style={styles.main}>
          <p style={styles.statusText}>Loading employee...</p>
        </main>
      </div>
    );
  }

  if (!employee) {
    return (
      <div style={styles.page}>
        <main style={styles.main}>
          <div style={styles.errorBanner}>{error || 'Employee not found.'}</div>
          <button onClick={() => navigate(-1)} style={styles.backButton}>← Go Back</button>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button onClick={() => navigate(-1)} style={styles.backButton}>
          ← Back
        </button>

        {error && <div style={styles.errorBanner}>{error}</div>}

        {/* Employee Info Card */}
        <div style={styles.card}>
          <div style={styles.empHeader}>
            <div style={styles.empLeft}>
              {employee.photo_url ? (
                <img src={employee.photo_url} alt={employee.full_name} style={styles.avatar} />
              ) : (
                <div style={styles.avatarInitials}>{getInitials(employee.full_name)}</div>
              )}
              <div style={styles.empMeta}>
                <h2 style={styles.empName}>{employee.full_name}</h2>
                <span style={styles.empRole}>{formatRole(employee.role)}</span>
                {employee.phone && <span style={styles.empPhone}>{employee.phone}</span>}
              </div>
            </div>
            <div style={styles.empActions}>
              <button
                onClick={() => navigate(`/employees/${employeeId}/edit`)}
                style={styles.editButton}
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeactivateConfirm(true)}
                style={styles.deactivateButton}
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>

        {/* Deactivate Confirm Dialog */}
        {showDeactivateConfirm && (
          <div style={styles.confirmOverlay}>
            <div style={styles.confirmDialog}>
              <h3 style={styles.confirmTitle}>Deactivate Employee?</h3>
              <p style={styles.confirmText}>
                Are you sure you want to deactivate <strong>{employee.full_name}</strong>? This action can be reversed by an admin.
              </p>
              <div style={styles.confirmActions}>
                <button
                  onClick={() => setShowDeactivateConfirm(false)}
                  disabled={deactivating}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeactivate}
                  disabled={deactivating}
                  style={styles.confirmDeactivateButton}
                >
                  {deactivating ? 'Deactivating...' : 'Yes, Deactivate'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Activity Timeline */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Activity Timeline</h3>
          {activities.length === 0 ? (
            <div style={styles.placeholder}>
              <p style={styles.placeholderText}>No activity recorded yet.</p>
            </div>
          ) : (
            <div style={styles.timeline}>
              {activities.map((act) => (
                <div key={act.id} style={styles.timelineItem}>
                  <div style={{
                    ...styles.timelineDot,
                    background: activityColor(act.activity_type),
                  }}>
                    <span style={styles.timelineDotIcon}>{activityIcon(act.activity_type)}</span>
                  </div>
                  <div style={styles.timelineContent}>
                    <div style={styles.timelineRow}>
                      <span style={{
                        ...styles.activityBadge,
                        color: activityColor(act.activity_type),
                        background: activityColor(act.activity_type) + '14',
                      }}>
                        {act.activity_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                      <span style={styles.timelineTime}>{formatTimestamp(act.created_at)}</span>
                    </div>
                    {act.notes && <p style={styles.timelineNotes}>{act.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Log Activity Form */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Log Activity</h3>
          <div style={styles.card}>
            {submitError && <div style={styles.formError}>{submitError}</div>}
            <form onSubmit={handleLogActivity} style={styles.form}>
              <div style={styles.formRow}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Activity Type</label>
                  <select
                    value={activityType}
                    onChange={(e) => setActivityType(e.target.value)}
                    disabled={submitting}
                    style={styles.select}
                  >
                    {ACTIVITY_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ ...styles.fieldGroup, flex: 1 }}>
                  <label style={styles.label}>Notes</label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes..."
                    disabled={submitting}
                    style={styles.input}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  style={styles.submitButton}
                >
                  {submitting ? 'Logging...' : 'Log Activity'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-base)',
  },
  main: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '32px 24px',
    fontFamily: 'var(--font-body)',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: 'var(--accent-text)',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    padding: 0,
    marginBottom: 24,
    fontFamily: 'var(--font-body)',
  },
  statusText: {
    color: 'var(--text-secondary)',
    fontSize: 14,
    fontFamily: 'var(--font-body)',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '12px 16px',
    borderRadius: 8,
    fontSize: 13,
    border: '1px solid rgba(255,71,87,0.3)',
    marginBottom: 20,
    fontFamily: 'var(--font-body)',
  },
  card: {
    background: 'var(--bg-surface)',
    borderRadius: 12,
    border: '1px solid var(--border-subtle)',
    padding: '24px 28px',
  },
  empHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  empLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    objectFit: 'cover',
  },
  avatarInitials: {
    width: 56,
    height: 56,
    borderRadius: 28,
    background: 'var(--accent-muted)',
    color: 'var(--accent-text)',
    fontSize: 20,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontFamily: 'var(--font-display)',
  },
  empMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  empName: {
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: 0,
    fontFamily: 'var(--font-display)',
  },
  empRole: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
  },
  empPhone: {
    fontSize: 13,
    color: 'var(--text-tertiary)',
    fontFamily: 'var(--font-body)',
  },
  empActions: {
    display: 'flex',
    gap: 10,
  },
  editButton: {
    padding: '8px 20px',
    borderRadius: 8,
    border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-raised)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-primary)',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
  },
  deactivateButton: {
    padding: '8px 20px',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--status-critical)',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
  },
  // Confirm dialog
  confirmOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(10,10,12,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  confirmDialog: {
    background: 'var(--bg-surface-overlay)',
    borderRadius: 12,
    padding: '28px 32px',
    maxWidth: 420,
    width: '90%',
    boxShadow: 'var(--shadow-lg)',
  },
  confirmTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: '0 0 10px',
    fontFamily: 'var(--font-display)',
  },
  confirmText: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
    margin: '0 0 20px',
    fontFamily: 'var(--font-body)',
  },
  confirmActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelButton: {
    padding: '8px 20px',
    borderRadius: 8,
    border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-raised)',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text-primary)',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
  },
  confirmDeactivateButton: {
    padding: '8px 20px',
    borderRadius: 8,
    border: 'none',
    background: 'var(--status-critical)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
  },
  // Sections
  section: {
    marginTop: 28,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: '0 0 14px',
    fontFamily: 'var(--font-display)',
  },
  placeholder: {
    background: 'var(--bg-surface)',
    borderRadius: 10,
    border: '1px solid var(--border-subtle)',
    padding: '36px 24px',
    textAlign: 'center',
  },
  placeholderText: {
    color: 'var(--text-secondary)',
    fontSize: 14,
    margin: 0,
    fontFamily: 'var(--font-body)',
  },
  // Timeline
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  timelineItem: {
    display: 'flex',
    gap: 14,
    padding: '12px 0',
    borderBottom: '1px solid var(--border-subtle)',
    borderLeft: '2px solid var(--border-default)',
    paddingLeft: 14,
  },
  timelineDot: {
    width: 28,
    height: 28,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
  },
  timelineDotIcon: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
  },
  timelineContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  timelineRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  activityBadge: {
    fontSize: 12,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 4,
    fontFamily: 'var(--font-body)',
  },
  timelineTime: {
    marginLeft: 'auto',
    fontSize: 12,
    color: 'var(--text-tertiary)',
    fontFamily: 'var(--font-mono)',
  },
  timelineNotes: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    margin: 0,
    lineHeight: 1.4,
    fontFamily: 'var(--font-body)',
  },
  // Log activity form
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  formRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-end',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-body)',
  },
  select: {
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid var(--border-default)',
    fontSize: 14,
    outline: 'none',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    appearance: 'auto' as any,
    colorScheme: 'dark' as any,
    fontFamily: 'var(--font-body)',
  },
  input: {
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid var(--border-default)',
    fontSize: 14,
    outline: 'none',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    colorScheme: 'dark' as any,
    fontFamily: 'var(--font-body)',
  },
  submitButton: {
    padding: '10px 20px',
    borderRadius: 8,
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'var(--font-body)',
  },
  formError: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 12,
    fontFamily: 'var(--font-body)',
  },
};
